import _ from 'underscore';
import React, { useContext, useState, useCallback, useEffect, useMemo } from 'react';
import { Body1Stronger, Body1, tokens, shorthands, makeStyles, Checkbox, Label, RadioGroup, Radio } from '@fluentui/react-components';
import PropTypes from 'prop-types';
import { I18nContext } from '@bingads-webui-react/i18n-context';
import { withCustomValidation } from '@bingads-webui-react/validation';
import { CheckOOInventoryBlocklists } from './check-oo-inventory-blocklists';
import { contentTargetingGroups } from './content-targeting-groups';

const useContentTargetingStyles = makeStyles({
  contentTargetingSpecificOptionsContainerStyle: {
    flexDirection: 'column',
  },
  contentTargetingSpecificOptionContainerStyle: {
    display: 'flex',
    ...shorthands.padding(tokens.spacingHorizontalXS, tokens.spacingHorizontalNone, tokens.spacingVerticalXS, tokens.spacingHorizontalNone),
    ...shorthands.gap(tokens.spacingVerticalS),
    ...shorthands.borderBottom(tokens.strokeWidthThin, 'solid', tokens.colorNeutralStroke1),
  },
  controlWrapperStyle: {
    width: '100%',
    '>label': {
      width: '100%',
      marginTop: '-2px',
    },
  },
  labelDescriptionStyle: {
    fontSize: '12px',
    color: tokens.colorNeutralForeground3,
  },
  entireSpecificSelectionStyle: {
    paddingBottom: '16px',
  },
});

const ENTIRE_MICROSOFT_NETWORK = 'entire';
const SPECIFIC_PLACEMENTS = 'specific';
const EXCLUDED_PLACEMENT_IDS = [3, 4, 7];

const isTheEntireMicrosoftNetworkSelected = placements => _.isEmpty(placements);

const EntireNetworkOrSpecificSelection = React.memo(({ entireSpecificSelection, onChangeEntireSpecific }) => {
  const i18n = useContext(I18nContext);
  const styles = useContentTargetingStyles();
  const allPlacementsLabel = i18n.getString(_TL_('The entire Microsoft Advertising Network (recommended)'));
  const allPlacementsSubLabel = i18n.getString(_TL_('This option includes Microsoft sites and additional partner traffic for extended reach and lower cost per click.'));
  const specificPlacementsLabel = i18n.getString(_TL_('Microsoft sites'));
  const specificPlacementsSubLabel = i18n.getString(_TL_('Show your ads on specific Microsoft placements.'));

  return (
    <RadioGroup
      onChange={onChangeEntireSpecific}
      value={entireSpecificSelection}
      className={styles.entireSpecificSelectionStyle}
    >
      <Radio
        className={styles.controlWrapperStyle}
        label={
          <>
            <Body1Stronger>
              {allPlacementsLabel}
            </Body1Stronger>
            <br />
            <Body1>
              {allPlacementsSubLabel}
            </Body1>
          </>
        }
        value={ENTIRE_MICROSOFT_NETWORK}
      />
      <Radio
        className={styles.controlWrapperStyle}
        label={
          <>
            <Body1Stronger>
              {specificPlacementsLabel}
            </Body1Stronger>
            <br />
            <Body1>
              {specificPlacementsSubLabel}
            </Body1>
          </>
        }
        value={SPECIFIC_PLACEMENTS}
      />
    </RadioGroup>
  );
});

EntireNetworkOrSpecificSelection.propTypes = {
  entireSpecificSelection: PropTypes.string.isRequired,
  onChangeEntireSpecific: PropTypes.func.isRequired,
};

const ContentTargetingSpecificPlacements = React.memo(({ groups, selectedPlacements, onToggleContentTargetingPlacement }) => {
  const isOnlyOnePlacementSelected = useMemo(() => (selectedPlacements || []).length === 1, [selectedPlacements]);
  const i18n = useContext(I18nContext);
  const styles = useContentTargetingStyles();
  return (
    <>
      <CheckOOInventoryBlocklists i18n={i18n} />
      {groups.map((group) => {
        const isChecked = selectedPlacements.includes(group.placementId);
        const isDisabled = isOnlyOnePlacementSelected && isChecked;
        return (
          <div
            key={group.placementId}
            className={styles.contentTargetingSpecificOptionContainerStyle}
          >
            <Checkbox
              checked={isChecked}
              onChange={() => onToggleContentTargetingPlacement(group.placementId)}
              disabled={isDisabled}
              className={styles.controlWrapperStyle}
              label={
                <>
                  <Body1Stronger>
                    {i18n.getString(group.name)}
                  </Body1Stronger>
                  <br />
                  <Body1 className={styles.labelDescriptionStyle}>
                    {i18n.getString(group.description)}
                  </Body1>
                </>
              }
            />
          </div>
        );
      })}
    </>
  );
});

ContentTargetingSpecificPlacements.propTypes = {
  groups: PropTypes.arrayOf(PropTypes.shape({
    placementId: PropTypes.number.isRequired,
    name: PropTypes.string.isRequired,
    description: PropTypes.string.isRequired,
  })).isRequired,
  selectedPlacements: PropTypes.arrayOf(PropTypes.number).isRequired,
  onToggleContentTargetingPlacement: PropTypes.func.isRequired,
};

const ContentTargetingPlacementsInternal = ({ updateValidation, validationProps, msnSubcategoriesEnabled }) => {
  const i18n = useContext(I18nContext);
  const placementsSectionLabel = i18n.getString(_TL_('Where do you want to show your ads?'));
  const placementOptionsLabel = i18n.getString(_TL_('Choose the specific Microsoft placements'));
  const filteredContentTargetingGroups = msnSubcategoriesEnabled ? contentTargetingGroups : contentTargetingGroups.filter(({ placementId }) => !EXCLUDED_PLACEMENT_IDS.includes(placementId));

  const { data: { placements } } = validationProps;

  const [entireSpecificSelection, setEntireSpecificSelection] = useState(isTheEntireMicrosoftNetworkSelected(placements) ? ENTIRE_MICROSOFT_NETWORK : SPECIFIC_PLACEMENTS);

  const selectedPlacements = useMemo(() => (placements ? placements.map(v => v.PlacementId) : []), [placements]);

  useEffect(() => {
    const radioSelection = isTheEntireMicrosoftNetworkSelected(placements) ? ENTIRE_MICROSOFT_NETWORK : SPECIFIC_PLACEMENTS;
    setEntireSpecificSelection(radioSelection);
  }, [setEntireSpecificSelection, placements]);

  const onChangeEntireSpecific = useCallback((_e, data) => {
    setEntireSpecificSelection(data.value);
    if (data.value === SPECIFIC_PLACEMENTS) {
      updateValidation([], {
        placements: filteredContentTargetingGroups.map(v => ({ PlacementId: v.placementId })),
      });
    } else {
      updateValidation([], {
        placements: [],
      });
    }
  }, [updateValidation, setEntireSpecificSelection, filteredContentTargetingGroups]);

  const onToggleContentTargetingPlacement = useCallback((placementId) => {
    if (selectedPlacements.includes(placementId)) {
      updateValidation([], {
        placements: selectedPlacements.filter(v => v !== placementId).map(v => ({ PlacementId: v })),
      });
    } else {
      updateValidation([], {
        placements: [...selectedPlacements, placementId].map(v => ({ PlacementId: v })),
      });
    }
  }, [updateValidation, selectedPlacements]);
  const styles = useContentTargetingStyles();
  return (
    <div id="placements-target-view">
      <Label>{placementsSectionLabel}</Label>
      <EntireNetworkOrSpecificSelection
        entireSpecificSelection={entireSpecificSelection}
        onChangeEntireSpecific={onChangeEntireSpecific}
      />
      {entireSpecificSelection === SPECIFIC_PLACEMENTS && (
        <div key="contentTargetingSpecificOptionsContainer" className={styles.contentTargetingSpecificOptionsContainerStyle}>
          <Label size="regular" weight="regular">{placementOptionsLabel}</Label>
          <ContentTargetingSpecificPlacements
            groups={filteredContentTargetingGroups}
            selectedPlacements={selectedPlacements}
            onToggleContentTargetingPlacement={onToggleContentTargetingPlacement}
          />
        </div>)}
    </div>
  );
};

ContentTargetingPlacementsInternal.propTypes = {
  updateValidation: PropTypes.func.isRequired,
  validationProps: PropTypes.shape({
    data: PropTypes.shape({
      placements: PropTypes.arrayOf(PropTypes.shape([])),
    }).isRequired,
  }).isRequired,
  msnSubcategoriesEnabled: PropTypes.bool.isRequired,
};

export const getContentTargetingPlacementsSummaryText = ({ i18n, placements }) => (
  isTheEntireMicrosoftNetworkSelected(placements)
    ? i18n.getString(_TL_('The entire Microsoft Advertising Network'))
    : i18n.getString(_TL_('Microsoft sites')));

export const ContentTargetingPlacements = withCustomValidation(ContentTargetingPlacementsInternal);
